import { z } from 'zod';

export const MenuActionSchema = z.object({
    id: z.string(),
    label: z.string(),
    icon: z.any().optional().nullable().default(null),
    iconColorComponent: z.any().optional().nullable().default(null),
    onClick: z.function().nullable().optional().default(null),
    disabled: z.boolean().optional().default(false),
    visible: z.boolean().optional().default(true),
    subMenu: z.any().nullable().optional().default(null),
    subMenuIsOpen: z.boolean().optional().default(false),
    visibleInContextMenu: z.boolean().default(true),
});
export type MenuActionType = z.infer<typeof MenuActionSchema>;


export type AvailableContextMenuModeType = 'menu' | 'tooltip';

export class MenuContextCustomActions {
    private actions: MenuActionType[] = [];
    private _isOpen: boolean = false;
    private _x: number = 0;
    private _y: number = 0;
    private _mode: AvailableContextMenuModeType = 'menu';

    reset() {
        this.actions = [];
    }
    
    getMode(): AvailableContextMenuModeType {
        return this._mode;
    }

    setCoords(x: number, y: number) {
        this._x = x;
        this._y = y;
    }

    get x() {
        return this._x;
    }

    get y() {
        return this._y;
    }

    open(menuMode: AvailableContextMenuModeType): this {
        this._isOpen = true;
        this._mode = menuMode;
        return this;
    }

    getActions() {
        return this.actions;
    }

    getActionsForContextMenu() {
        return this.actions.filter(action => action.visibleInContextMenu);
    }

    init(actions: MenuActionType[]): this {
        this.actions = actions;
        this._isOpen = false;
        return this;
    }

    get isOpen(): boolean {
        return this._isOpen;
    }

    set isOpen(v: boolean) {
        this._isOpen = v;
    }

    addAction(action: MenuActionType): this {
        action = MenuActionSchema.parse(action);
        this.actions.push(action);
        return this;
    }

    getVisibleActions() {
        return this.actions.filter(action => action.visible);
    }

    isAnyIcon() {
        return this.actions.some(action => action.icon !== null);
    }

    hideItem(id: string) {
        const action = this.actions.find(action => action.id === id);
        if (action) {
            action.visible = false;
        }
    }

    showItem(id: string) {
        const action = this.actions.find(action => action.id === id);
        if (action) {
            action.visible = true;
        }
    }

    triggerAction(id: string) {
        const action = this.actions.find(action => action.id === id);
        if (action && action.onClick) {
            if (action.disabled) {
                console.error('Action ' + id + 'is disabled');
                return;
            }
            if (!action.visible) {
                console.error('Action ' + id + 'is not visible');
                return;
            }
            action.onClick();
        } else {
            console.error('Action ' + id + ' not found or has no onClick method');
        }
    }

    isAvailable(id: string) {
        const action = this.actions.find(action => action.id === id);
        if (action) {
            return action.visible && !action.disabled;
        }
        return false;
    }

    isValid(): boolean {
        return Array.isArray(this.actions) && this.actions.length > 0;
    }
    
    hasVisibleItems(): boolean {
        return this.actions.some(action => action.visible);
    }
    
}
